import {
    Box,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { copyValue } from '@utils'
import useGroupPage from './useGroupPage'
import GroupUserTable from './GroupUserTable'
import SectionContainer from '@components/SectionContainer'
import GroupSettingPane from './GroupSettingPane'
import GroupLimitPane from './GroupLimitPane'
import { DateTime } from 'luxon'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

export default function GroupPage(): JSX.Element {
    const { loading, error, errMessage, group } = useGroupPage()

    if (loading) {
        return <DashboardLayout loading></DashboardLayout>
    }

    if (error) {
        return (
            <DashboardLayout>
                <Typography variant="body1">{errMessage}</Typography>
            </DashboardLayout>
        )
    }

    if (!group) {
        return (
            <DashboardLayout>
                <Typography variant="body1">No Group Loaded!</Typography>
            </DashboardLayout>
        )
    }

    return (
        <DashboardLayout>
            <Paper>
                <Typography variant="h1">{group.name}</Typography>
                <Typography variant="subtitle1">
                    {group.id}
                    <Tooltip title="Copy ID" color="textSecondary">
                        <IconButton size="small" onClick={copyValue(group.id)}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Typography>
                <Typography variant="body1">
                    Organization ID: {group.org_id}
                    <Tooltip title="Copy ID" color="textSecondary">
                        <IconButton
                            onClick={copyValue(group.org_id)}
                            size="small"
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title="Open Organization in new tab"
                        color="textSecondary"
                    >
                        <IconButton
                            size="small"
                            href={`/organizations/${group.org_id}`}
                            target="_blank"
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Typography>
                <Typography variant="body1">
                    Created at:{' '}
                    {DateTime.fromISO(group.created_at).toFormat('DD HH:mm:ss')}
                </Typography>
                <Typography variant="body1">
                    Updated at:{' '}
                    {DateTime.fromISO(group.updated_at).toFormat('DD HH:mm:ss')}
                </Typography>
                {group.deleted_at && (
                    <Typography variant="body1">
                        Deleted at:{' '}
                        {DateTime.fromISO(group.deleted_at).toFormat(
                            'DD HH:mm:ss'
                        )}
                    </Typography>
                )}
                <Box sx={{ maxWidth: '80ex', my: '1em' }}>
                    <Typography variant="h2">Description</Typography>
                    <Typography variant="body1">{group.description}</Typography>
                </Box>
                <SectionContainer title={`Users (${group.users.length})`}>
                    <GroupUserTable users={group.users} />
                </SectionContainer>
                {group.service && (group.serviceSetting || group.limit) && (
                    <SectionContainer title="Summary Notes">
                        <Grid container spacing={1}>
                            {group.serviceSetting && (
                                <Grid item sm={12} md={6}>
                                    <GroupSettingPane
                                        service={group.service}
                                        serviceSetting={group.serviceSetting}
                                    />
                                </Grid>
                            )}
                            {group.limit && (
                                <Grid item sm={12} md={6}>
                                    <GroupLimitPane limit={group.limit} />
                                </Grid>
                            )}
                        </Grid>
                    </SectionContainer>
                )}
            </Paper>
        </DashboardLayout>
    )
}
